/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { AssetSubClassCode, Entity, formatTicker } from '@api';
import { ChipHorizonSelector, RangeHorizon } from '@chart';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Toggle } from '@design/components/toggle/Toggle';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import {
  SNAKES_NTM,
  SNAKES_TTM,
} from '@terminal/modules/overview-valuation-metrics/utils';
import { queryKeys } from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { i18n as i18nTerminal } from '@terminal/utils/i18n/i18n';
import { useChartLocales } from '@terminal/views/turbo-chart/hooks/use-chart-locales/useChartLocales';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { UseTurboChartReturn } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import React, { ChangeEvent, createRef, RefObject, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import styles from './ChartPeerGroup.module.scss';
import { ChartPerSnake } from './ChartPerSnake/ChartPerSnake';
import { ChipsSelector } from './components/chips-selector/ChipsSelector';
import { SnakeTypes } from './utils/chart-subpage-navigation/chart-subpage-navigation';

export interface ChartPeerGroupProps {
  entity: Pick<
    Entity,
    'tag' | 'default_snake' | 'ticker' | 'exchange' | 'sub_class'
  >;
  variant?: 'kg_competitors_drawer' | 'overview';
  peerEntities: Entity[];
  isFetching: boolean;
}

enum ValuationTypes {
  TTM = 'TTM',
  NTM = 'NTM',
}

const variants = {
  [ValuationTypes.TTM]: SNAKES_TTM,
  [ValuationTypes.NTM]: SNAKES_NTM,
};

const defaultSnakes = ['price'];

const variantToPosthogSourceMap: Record<
  NonNullable<ChartPeerGroupProps['variant']>,
  PosthogTrackingSource
> = {
  overview: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
  kg_competitors_drawer: POSTHOG_TRACKING_SOURCES.COMPETITORS_DRAWER,
};

export interface ChartPerSnakeRef {
  changeRange: UseTurboChartReturn['changeRange'];
  hideActiveAsset: UseTurboChartReturn['hideActiveAsset'];
}

export const ChartPeerGroup = ({
  entity,
  variant = 'overview',
  peerEntities,
  isFetching,
}: ChartPeerGroupProps) => {
  const { t } = useTranslation(['widget', 'chart', 'analyze', 'countries']);
  const { params, deleteQueryParam } = useQueryParams();
  const { horizonLocales: chipHorizonSelectorLocales } = useChartLocales();

  const isKGCompetitorsDrawer = variant === 'kg_competitors_drawer';
  const defaultChartHorizon = isKGCompetitorsDrawer
    ? RangeHorizon.OneYear
    : RangeHorizon.ThreeMonths;
  const horizonParam = (params[queryKeys.horizon] ??
    defaultChartHorizon) as RangeHorizon;
  const snakeTypeParam = params[queryKeys.snakeType] as SnakeTypes | null;

  const [showPeerGroup, setShowPeerGroup] = useState(true);
  const [selectedSnakeTypes, setSelectedSnakeTypes] = useState(
    snakeTypeParam ?? SnakeTypes.Price
  );
  const [activeValuation, setActiveValuation] = useState(ValuationTypes.TTM);
  const [entitiesVisibility, setEntitiesVisibility] = useState<
    Record<string, boolean>
  >({});
  const [horizon, setHorizon] = useState(horizonParam);
  const { colors } = useChartTheme(['multiline']);

  const showValuationMetrics =
    selectedSnakeTypes === SnakeTypes.ValuationMetrics;
  const snakeNames = variants[activeValuation];
  const isPriceSnakeSelected = selectedSnakeTypes === SnakeTypes.Price;
  const allSnakes = isPriceSnakeSelected ? defaultSnakes : snakeNames;
  const allEntities = showPeerGroup ? [entity, ...peerEntities] : [entity];
  const chartsPerSnake = allSnakes.map(snake => ({
    snake,
    assets: allEntities.map(entity => ({
      tag: entity.tag,
      snake: isPriceSnakeSelected
        ? entity.default_snake
        : `${entity.tag}.${snake}`,
      paneIndex: 0,
    })),
  }));

  const chartRefs = chartsPerSnake.map(
    () => createRef() as RefObject<ChartPerSnakeRef>
  );

  const onCheckboxChange = (
    entityTag: string,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    chartRefs.forEach((ref, index) => {
      const snake = `${entityTag}.${allSnakes[index]}`;
      ref.current?.hideActiveAsset(snake, { isHidden: !event.target.checked });
    });

    setEntitiesVisibility(prev => ({
      ...prev,
      [entityTag]: !event.target.checked,
    }));
  };

  const onHorizonChange = (horizon: RangeHorizon) => {
    chartRefs.forEach(ref => {
      ref.current?.changeRange(horizon);
    });

    setHorizon(horizon);
    deleteQueryParam(queryKeys.horizon);
  };

  const onToggle = (checked: boolean) => {
    setShowPeerGroup(checked);
    setEntitiesVisibility({});
  };

  if (isFetching) {
    return (
      <div className={styles.skeletonWrapper} data-testid="chart-loading">
        <div className={styles.skeletonChartLoaderWrapper}>
          <SkeletonLoader
            areas={[{ styles: { width: '100%', borderRadius: '8px' } }]}
          />
        </div>
      </div>
    );
  }

  const isStockAsset = entity.sub_class === AssetSubClassCode.EquityStock;
  const snakeTypeItems = [
    {
      id: SnakeTypes.Price,
      label: t('widget:assetOverview.chartTab.snakeTypesSelector.price'),
    },
    {
      id: SnakeTypes.ValuationMetrics,
      label: t(
        'widget:assetOverview.chartTab.snakeTypesSelector.valuationMetrics'
      ),
    },
  ];
  const valuationItems = [
    {
      id: ValuationTypes.TTM,
      label: t('widget:assetOverview.ttm'),
    },
    {
      id: ValuationTypes.NTM,
      label: t('widget:assetOverview.ntm'),
    },
  ];

  const multiColors = colors.getMulti(allEntities.length);
  return (
    <div className={styles.chartPeerGroupRoot} data-testid="chart-peer-group">
      <section className={styles.body}>
        <I18nextProvider i18n={i18nTerminal}>
          {!isKGCompetitorsDrawer && (
            <div className={styles.toolbar}>
              <div className={styles.groupSection}>
                {isStockAsset && (
                  <ChipsSelector
                    data-testid="snake-type-selector"
                    items={snakeTypeItems}
                    selectedId={selectedSnakeTypes}
                    onChange={setSelectedSnakeTypes}
                  />
                )}
                <ChipHorizonSelector
                  horizonRanges={Object.values(RangeHorizon)}
                  selectedRange={horizon}
                  onChange={onHorizonChange}
                  locales={chipHorizonSelectorLocales}
                />
              </div>
              <div className={styles.groupSection}>
                {showValuationMetrics && (
                  <ChipsSelector
                    data-testid="valuation-type-selector"
                    items={valuationItems}
                    selectedId={activeValuation}
                    onChange={setActiveValuation}
                  />
                )}
                {!!peerEntities.length && (
                  <Toggle
                    leftLabel={t(
                      'widget:assetOverview.chartTab.peerGroupToggle'
                    )}
                    isChecked={showPeerGroup}
                    size="small"
                    onToggle={onToggle}
                    data-testid="peer-toggle"
                  />
                )}
              </div>
            </div>
          )}

          <div className={styles.checkboxContainer} data-checkbox-container>
            {allEntities.map((entity, index) => (
              <div
                key={entity.tag}
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                style={
                  {
                    '--surface-primary': multiColors[index].value,
                    '--border-primary': multiColors[index].value,
                  } as React.CSSProperties
                }
              >
                <CheckBox
                  name={entity.tag}
                  label={formatTicker(entity)}
                  checked={!entitiesVisibility[entity.tag]}
                  onChange={onCheckboxChange}
                  variant={allEntities.length === 1 ? 'disabled' : 'primary'}
                />
              </div>
            ))}
          </div>
          <div className={styles.chartContent} data-chart-content>
            {chartsPerSnake.map((item, index) => (
              <ChartPerSnake
                key={item.snake}
                chartAssets={item.assets}
                colors={colors}
                horizon={horizon}
                chartRef={chartRefs[index]}
                isSingle={chartsPerSnake.length === 1}
                initialPriceDisplay={
                  (showPeerGroup && !!peerEntities.length) ||
                  !isPriceSnakeSelected
                    ? 'percentage'
                    : 'price'
                }
                withHeader={!isKGCompetitorsDrawer}
                enablePriceHover={!isKGCompetitorsDrawer}
                hideYAxisLines={isKGCompetitorsDrawer}
                displayAxisUnit={false}
                source={variantToPosthogSourceMap[variant]}
              />
            ))}
          </div>
        </I18nextProvider>
      </section>
    </div>
  );
};
